import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Pagination} from '../../shared/Pagination';
import {PageHeader} from '../../shared/PageHeader';
import {MakeApiCall, WriteConsoleErrors, ToString, ObjectToQueryString, ReplaceUrl} from '../../shared/utils';
import {FlagComponent} from './flag';
import {AccountComponent} from './account';
import {SimpleFilterItemComponent} from '../SimpleFilterItem';

import './styles.scss';

const ACCOUNTS_ACTION = {
    NONE: 0,
    LOAD: 1
}

export class AccountsListComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            isLoaded: false,
            accountFilter: props.accountFilter,
            agentFilter: props.agentFilter,
            jobFilter: props.jobFilter,
            deletedStateId: props.deletedStateId,
            accountRoleTypeId: props.accountRoleTypeId,
            pageNum: props.pageNum,
            pageSize: props.pageSize,
            action: ACCOUNTS_ACTION.NONE,
            totalCount: 0,
            errors: null,
            accounts: [],
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.renderTable = this.renderTable.bind(this);
        this.renderPagination = this.renderPagination.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {
            accf: self.state.accountFilter,
            agtf: self.state.agentFilter,
            jobf: self.state.jobFilter,
            ds: self.state.deletedStateId,
            rt: self.state.accountRoleTypeId,
            pn: self.state.pageNum,
            ps: self.state.pageSize
        }

        new Promise(resolve => {
            self.setState({action: ACCOUNTS_ACTION.LOAD, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                ReplaceUrl({
                    pathname: window.location.pathname,
                    search: ObjectToQueryString(data, true, {pn:1, ps: 50})
                });

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAccounts',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: ACCOUNTS_ACTION.NONE,
                    isLoaded: true,
                    accountFilter: response.accf,
                    agentFilter: response.agtf,
                    jobFilter: response.jobf,
                    deletedStateId: response.ds,
                    accountRoleTypeId: response.rt,
                    pageNum: response.pn,
                    pageSize: response.ps,
                    accounts: response.Accounts,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: ACCOUNTS_ACTION.NONE, errors: errors})
            }
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doLoad();
    }

    renderForm() {
        const self = this;
        const {action, accountFilter, agentFilter, jobFilter, deletedStateId, accountRoleTypeId} = self.state;
        const isDisabled = action != ACCOUNTS_ACTION.NONE;

        const _renderFilterLink = (filter, negativeFilter, filterPattern, caption) => {
            return (
                <SimpleFilterItemComponent
                    disabled={isDisabled}
                    filters={accountFilter}
                    filter={filter}
                    detectPattern={filterPattern}
                    negativeDetectPattern={negativeFilter}
                    caption={caption}
                    onChange={f => self.setState({accountFilter: f})}
                />
            )
        }

        return (
            <div className="mb-n2 mt-2">
                <form className="form-inline mr-n2" onSubmit={self.handleSubmit}>
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AccountId, Email, Name, Company, :filter"
                        style={{width: '514px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(accountFilter)}
                        onChange={e => self.setState({accountFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AgentId, AgentKey, Name"
                        style={{width: '290px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(agentFilter)}
                        onChange={e => self.setState({agentFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="JobId, Name"
                        style={{width: '290px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(jobFilter)}
                        onChange={e => self.setState({jobFilter: e.target.value})}
                    />
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={deletedStateId ?? ''}
                        onChange={e => self.setState({deletedStateId: e.target.value})}
                    >
                        {self.props.deletedStates.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={accountRoleTypeId ?? ''}
                        onChange={e => self.setState({accountRoleTypeId: e.target.value})}
                    >
                        {self.props.accountRoleTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <button 
                        type="submit"
                        className="btn btn-outline-primary mb-2 mr-2"
                        disabled={isDisabled}
                    >
                        <span className="fa fa-search mr-1" />
                        Search
                        <ButtonBusyIndicatorComponent visible={action == ACCOUNTS_ACTION.LOAD} />
                    </button>
                </form>
                <div className="small">
                    Filters:&nbsp;
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':cc', ':ncc')} {_renderFilterLink(':ncc', ':cc')} — with cc or not;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':pro')}, {_renderFilterLink(':std')}, {_renderFilterLink(':free')} — plans (OR condition);</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':trial', ':ntrial')}, {_renderFilterLink(':ntrial', ':trial')} — in trial or not;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':ref', ':nref')} {_renderFilterLink(':nref', ':ref')} — linked to affiliate or not;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':vip', null, ':vip[0-9]*', ':vipX')} — X (default 5) or more servers with paid plan;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':susp', ':nsusp')}, {_renderFilterLink(':nsusp', ':susp')} — suspended or not;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':c-us')}, {_renderFilterLink(':c-ru')}, <span>:c-??</span> — country (OR condition);</span>
                </div>
            </div>
        )
    }

    renderTable() {
        const self = this;
        const {action, accounts} = self.state;
        const {timeZoneId} = self.props;
        
        const tzNow = moment.utc().tz(timeZoneId);
        const tzName = tzNow.format('z');
        const existsNew = accounts.some(p => tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day'));

        return (
            <table className="accounts table-striped-even table-hover table-responsive newstyle">
                <thead>
                    <tr>
                        {existsNew && <th width="1%" />}
                        <th width="1%">Account#</th>
                        <th width="20%">Name</th>
                        <th width="1%" className="text-center"><span className="fa fa-globe" /></th>
                        <th width="15%">Email</th>
                        <th width="20%">Company</th>
                        <th width="1%" className="text-right">Servers</th>
                        <th width="1%" className="text-right">Jobs</th>
                        <th width="41%" style={{minWidth: '175px'}}>Plan</th>
                        <th width="1%" className="text-nowrap">Created ({tzName})</th>
                        <th width="1%" className="text-nowrap">Role</th>
                    </tr>
                </thead>
                <tbody>
                    {accounts.map(p => {
                        let rowClass = '';
                        if (Boolean(p.DeletedAt)) {
                            rowClass = 'danger';
                        }
                        else if (Boolean(p.SuspendedAt)) {
                            rowClass = 'warning';
                        }
                        let servicePlan = p.Subscription.ServicePlanName;
                        if (p.Subscription.IsTrial) {
                            servicePlan += ' trial';
                        }
                        if (p.Subscription.ServicePlanTypeId > 1) {
                            servicePlan += ' till ' + moment.utc(p.Subscription.RestartAt).tz(timeZoneId).format('L');
                            servicePlan += ', ' + numeral(p.Subscription.BasePrice).USD();
                        }

                        return (
                            <tr key={'account-' + p.Id} className={rowClass}>
                                {existsNew && 
                                    <td>
                                        {tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day') && <span className="fa fa-flash text-success" />}
                                    </td>
                                }
                                <td>{p.Id}</td>
                                <td>
                                    <AccountComponent
                                        id={p.Id}
                                        name={p.Name}
                                        showLoginAs
                                        linkToInfo
                                    />
                                </td>
                                <td>
                                    <FlagComponent
                                        countryId={p.CountryId}
                                        countryName={p.CountryName}
                                        lastIP={p.LastIP}
                                    />
                                </td>
                                <td>{p.Email}</td>
                                <td>{p.Company}</td>
                                <td className="text-right text-nowrap">
                                    {p.OwnAgentsCount + (p.SharedAgentsCount > 0 ? `+${p.SharedAgentsCount}=${p.OwnAgentsCount + p.SharedAgentsCount}` : '')}
                                </td>
                                <td className="text-right text-nowrap">
                                    {p.OwnJobsCount + (p.SharedJobsCount > 0 ? `+${p.SharedJobsCount}=${p.OwnJobsCount + p.SharedJobsCount}` : '')}
                                </td>
                                <td>{servicePlan}</td>
                                <td className="text-nowrap">{moment.utc(p.CreatedAt).tz(timeZoneId).format('L LT')}</td>
                                <td className="text-nowrap">{p.RoleName}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    renderPagination() {
        const self = this;
        const {pageNum, pageSize, totalCount}  = self.state;

        const pagesCount = Math.ceil(totalCount / Math.max(1, pageSize));

        if (pagesCount <= 1) {
            return null;
        }

        return (
            <Pagination
                className="justify-content-end"
                pagesCount={pagesCount}
                pageNum={pageNum}
                maxButtons={7}
                onPageSelect={(pn) => self.setState({pageNum: pn}, self.doLoad)}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {totalCount, isLoaded, errors} = self.state;

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">Accounts</h2>
                    }
                >
                    {self.renderForm()}
                </PageHeader>
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && <div className="small">{`${totalCount} account(s) found`}</div>}
                {self.renderTable()}
                <div className="pt-3">
                    {self.renderPagination()}
                </div>
           </div>
        )
    }
}
AccountsListComponent.propTypes = {
    pageNum: PropTypes.number,
    pageSize: PropTypes.number,
    timeZoneId: PropTypes.string.isRequired,
    accountFilet: PropTypes.string,
    agentFilter: PropTypes.string,
    jobFilter: PropTypes.string,
    deletedStateId: PropTypes.string,
    deletedStates: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    accountRoleTypeId: PropTypes.string,
    accountRoleTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
}
AccountsListComponent.defaultProps = {
    pageNum: 1,
    pageSize: 50,
}
